/**
 * Vault editor connectivity hook — true when the Vault WS is connected.
 *
 * WS readyState is a stricter signal than `navigator.onLine`: the browser's
 * online flag lies often, and a working TCP link doesn't mean the DV server
 * is reachable. Used by EditorPane for its read-only banner and by
 * Workspace's StatusLight for the in-editor status dot.
 */

import { useEffect, useState } from 'react';
import { subscribeConnectivity, isConnected, subscribeHeartbeat } from '../sync/ws';
import { useVault } from '../../../core/lib/vault';
import { isTokenExpired, getAccessToken } from '../../../core/lib/api';

export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(isConnected());
  useEffect(() => subscribeConnectivity(setOnline), []);
  return online;
}

/**
 * Why are we degraded? When `useOnline()` is false, this disambiguates
 * which of the three "not durably syncing" states we're in so StatusLight
 * can show "Locked — N queued" / "Re-auth needed — N queued" / "Offline — N
 * queued" instead of a generic offline label. Returns 'online' when sync
 * is fully healthy.
 *
 * Order matches `isConnected()` so the displayed reason matches the gate
 * that's actually blocking sync.
 */
/**
 * Counter that increments on every successful WS pong — once every ~15 s
 * while the socket is bidirectionally healthy. StatusLight reads this to
 * fire a brief pulse animation: visible confirmation to the user that
 * we're not just nominally "connected" but actually round-tripping with
 * the server right now.
 */
export function useHeartbeatTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeHeartbeat(() => setTick((n) => n + 1)), []);
  return tick;
}

export type ConnectionMode = 'online' | 'locked' | 'expired' | 'offline';
export function useConnectionMode(): ConnectionMode {
  const [, force] = useState(0);
  useEffect(() => subscribeConnectivity(() => force((n) => n + 1)), []);
  // Re-render on locked ↔ unlocked transitions only. Subscribing without a
  // selector would fire on every vault state change (touch, autoLockMs
  // tweaks, etc.) and re-render the entire status indicator on every
  // keystroke since `touch()` runs on input.
  useEffect(
    () =>
      useVault.subscribe((s, prev) => {
        if ((s.vaultKey == null) !== (prev.vaultKey == null)) {
          force((n) => n + 1);
        }
      }),
    [],
  );
  if (isConnected()) return 'online';
  if (useVault.getState().vaultKey == null) return 'locked';
  if (getAccessToken() != null && isTokenExpired(5)) return 'expired';
  return 'offline';
}
